import type { DynamicDisplayTrigger, DynamicDisplayFrequencyUnit } from '@/types'

const STORAGE_PREFIX = 'widget-display-'

/**
 * Convert a frequency unit to milliseconds
 */
export function unitToMilliseconds(unit: DynamicDisplayFrequencyUnit): number {
  switch (unit) {
    case 'second':
      return 1000
    case 'minute':
      return 60 * 1000
    case 'hour':
      return 60 * 60 * 1000
    case 'day':
      return 24 * 60 * 60 * 1000
    case 'week':
      return 7 * 24 * 60 * 60 * 1000
    case 'month':
      return 30 * 24 * 60 * 60 * 1000
    case 'year':
      return 365 * 24 * 60 * 60 * 1000
    default:
      return 0
  }
}

/**
 * Get the storage key for a component's display frequency tracking
 */
function getStorageKey(key: string): string {
  return `${STORAGE_PREFIX}${key}-timestamp`
}

/**
 * Determines if a frequency config is meaningfully active.
 * - explicitly enabled (true) → active
 * - explicitly disabled (false) → inactive
 * - null/undefined enabled with count AND unit provided → active (configured but not toggled)
 * - null/undefined enabled without count/unit → inactive (unconfigured placeholder)
 */
export function isFrequencyActive(frequency?: DynamicDisplayTrigger): frequency is DynamicDisplayTrigger {
  if (!frequency || frequency.enabled === false) return false
  if (frequency.enabled === true) return true
  return frequency.count !== undefined && frequency.unit !== undefined
}

/**
 * Check if the component should be shown based on display frequency settings.
 * Returns true if enough time has passed since the last display.
 *
 * @param key - Unique identifier for the component (e.g., 'widget-icon', 'welcome-message')
 * @param frequency - The frequency trigger settings
 * @returns true if the component should be shown, false if it was shown too recently
 */
export function shouldShowByFrequency(key: string, frequency?: DynamicDisplayTrigger): boolean {
  if (!isFrequencyActive(frequency)) {
    return true
  }

  const count = frequency.count ?? 1
  const unit = frequency.unit ?? 'day'

  // Calculate the minimum time between displays
  const intervalMs = count * unitToMilliseconds(unit)

  // Get the last display timestamp from localStorage
  const storageKey = getStorageKey(key)
  const lastDisplayStr = localStorage.getItem(storageKey)

  if (!lastDisplayStr) {
    // Never shown before, allow
    return true
  }

  const lastDisplay = parseInt(lastDisplayStr, 10)
  if (Number.isNaN(lastDisplay)) {
    // Invalid stored value, allow and it will be overwritten
    return true
  }

  const now = Date.now()
  const elapsed = now - lastDisplay

  // Allow if enough time has passed
  return elapsed >= intervalMs
}

/**
 * Record that the component was displayed, updating the timestamp in localStorage.
 *
 * @param key - Unique identifier for the component
 */
export function recordDisplay(key: string): void {
  const storageKey = getStorageKey(key)
  localStorage.setItem(storageKey, Date.now().toString())
}

/**
 * Returns the remaining time in milliseconds before the component can be shown again.
 * Returns 0 if no frequency is set, frequency is disabled, or enough time has already passed.
 */
export function getFrequencyRemainingMs(key: string, frequency?: DynamicDisplayTrigger): number {
  if (!isFrequencyActive(frequency)) return 0

  const count = frequency.count ?? 1
  const unit = frequency.unit ?? 'day'
  const intervalMs = count * unitToMilliseconds(unit)

  const storageKey = getStorageKey(key)
  const lastDisplayStr = localStorage.getItem(storageKey)
  if (!lastDisplayStr) return 0

  const lastDisplay = parseInt(lastDisplayStr, 10)
  if (Number.isNaN(lastDisplay)) return 0

  return Math.max(0, intervalMs - (Date.now() - lastDisplay))
}

/**
 * Clear the display record for a component (useful for testing or reset).
 *
 * @param key - Unique identifier for the component
 */
export function clearDisplayRecord(key: string): void {
  const storageKey = getStorageKey(key)
  localStorage.removeItem(storageKey)
}

